import React from 'react';
import {
  faArrowsRotate,
  faExclamationTriangle,
  faChevronLeft,
  faChevronRight,
  faArrowDown,
  faArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AssessmentCard from './AssessmentCard';

import { Assessment, PaginationInfo } from '@/services/assessmentsAPI';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import LoadingState from '@/components/LoadingState';
import Alert from '@/components/Alert';
import { Body } from '@/components/Typography';

interface AssessmentsListProps {
  assessments: Assessment[];
  selectedAssessment: Assessment | null;
  onSelectAssessment: (assessment: Assessment) => void;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
  reviewed?: boolean;
  onReviewedToggle?: (reviewed: boolean) => void;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (order: 'asc' | 'desc') => void;
}

const AssessmentsList: React.FC<AssessmentsListProps> = ({
  assessments,
  selectedAssessment,
  onSelectAssessment,
  loading,
  error,
  onRefresh,
  pagination,
  onPageChange,
  reviewed,
  onReviewedToggle,
  sortOrder = 'desc',
  onSortChange,
}) => {
  const handleSortToggle = () => {
    if (onSortChange) {
      onSortChange(sortOrder === 'desc' ? 'asc' : 'desc');
    }
  };

  const handlePrevPage = () => {
    if (pagination && onPageChange && pagination.page > 1) {
      onPageChange(pagination.page - 1);
    }
  };

  const handleNextPage = () => {
    if (
      pagination &&
      onPageChange &&
      pagination.page < pagination.total_pages
    ) {
      onPageChange(pagination.page + 1);
    }
  };

  return (
    <Card changeStyle={{ padding: 24 }}>
      <Stack vertical gap={16} css={{ height: '100%' }}>
        <Stack gap={12} align="center" justify="apart" wrap>
          <Stack gap={12} align="center" justify="end" wrap>
            <button
              onClick={onRefresh}
              title="Refresh assessments list"
              aria-label="Refresh assessments list"
              disabled={loading}
              style={{
                background: 'none',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <FontAwesomeIcon icon={faArrowsRotate} />
            </button>

            {onSortChange && (
              <button
                onClick={handleSortToggle}
                title={`Sort by created date ${sortOrder === 'desc' ? 'ascending' : 'descending'}`}
                aria-label={`Sort by created date ${sortOrder === 'desc' ? 'ascending' : 'descending'}`}
                disabled={loading}
                style={{
                  background: 'none',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <FontAwesomeIcon
                  icon={sortOrder === 'desc' ? faArrowDown : faArrowUp}
                />
              </button>
            )}

            {onReviewedToggle !== undefined && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={reviewed || false}
                  onChange={(e) => onReviewedToggle(e.target.checked)}
                  disabled={loading}
                />
                <Body>Show Reviewed</Body>
              </label>
            )}
          </Stack>
        </Stack>

        <Stack vertical gap={8} css={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Stack vertical gap={16} align="center" css={{ padding: '24px' }}>
              <LoadingState text="Loading assessments..." />
            </Stack>
          ) : error ? (
            <Stack vertical gap={16} align="center" css={{ padding: '24px' }}>
              <Alert
                status="danger"
                icon={faExclamationTriangle}
                description={error}
              />
              <Button
                onClick={onRefresh}
                a11yLabel="Retry loading assessments"
                label="Retry"
              />
            </Stack>
          ) : assessments.length === 0 ? (
            <Stack vertical gap={16} align="center" css={{ padding: '24px' }}>
              <Body>No assessments found</Body>
              <Button
                onClick={onRefresh}
                a11yLabel="Refresh assessments list"
                label="Refresh"
              />
            </Stack>
          ) : (
            assessments.map((assessment) => (
              <AssessmentCard
                key={assessment.external_id}
                assessment={assessment}
                isSelected={
                  selectedAssessment?.external_id === assessment.external_id
                }
                onClick={() => onSelectAssessment(assessment)}
              />
            ))
          )}
        </Stack>

        {pagination && pagination.total_pages > 1 && (
          <Stack
            justify="apart"
            align="center"
            css={{ paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}
          >
            <Button
              onClick={handlePrevPage}
              a11yLabel="Previous page"
              iconLeft={faChevronLeft}
              label="Previous"
              disabled={pagination.page === 1}
            />
            <Body>
              Page {pagination.page} of {pagination.total_pages}
            </Body>
            <Button
              onClick={handleNextPage}
              a11yLabel="Next page"
              iconRight={faChevronRight}
              label="Next"
              disabled={pagination.page === pagination.total_pages}
            />
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default AssessmentsList;
